/**
 * Bucket a result's extracted fields by the page they were extracted from.
 *
 * Mirrors the backend's `SelectTemplatePoolWinnersJob::sliceByPage()`, which
 * owns per-page winner scoring for a template-pool run: a field is placed on
 * the page of the FIRST of its `references` that resolves to a known chunk,
 * and a field whose references resolve to nothing is left out of every page.
 *
 * The panel used to default those refless fields to page 1. That quietly made
 * PG-001 the dumping ground for every candidate template that matched the
 * document badly — a 12-page pool run showed three templates on page 1 and
 * exactly one on each other page, and the fields piled onto page 1 were the
 * same ones the backend had excluded from that page's confidence score.
 *
 * A refless field is no longer automatically pageless, though: the model
 * reports a page for every field it extracts, boxed or not, and the pipeline
 * carries that through as `source_page`. Only a field with no page signal at
 * all is returned in `unattributed`, so the caller can render it where it can
 * still be verified without claiming a page it never touched.
 */
export interface FieldPageBuckets<F> {
  /** 1-based page number → its fields, in extraction order. */
  byPage: Map<number, F[]>;
  /** Fields whose references resolve to no known page. */
  unattributed: F[];
}

export function bucketFieldsByPage<
  F extends {
    page_number?: number | null;
    references?: string[];
    source_page?: number | null;
  }
>(
  fields: readonly F[],
  chunkPageByChunkId: ReadonlyMap<string, number>
): FieldPageBuckets<F> {
  const byPage = new Map<number, F[]>();
  const unattributed: F[] = [];

  for (const field of fields) {
    const page = resolvePage(field, chunkPageByChunkId);
    if (page == null) {
      unattributed.push(field);
      continue;
    }
    const bucket = byPage.get(page);
    if (bucket) bucket.push(field);
    else byPage.set(page, [field]);
  }

  return { byPage, unattributed };
}

function resolvePage(
  field: {
    page_number?: number | null;
    references?: string[];
    source_page?: number | null;
  },
  chunkPageByChunkId: ReadonlyMap<string, number>
): number | null {
  // Repeating templates stamp the page onto the item itself (one form instance
  // per page), which is authoritative even when the field resolved no chunk.
  if (field.page_number != null) return field.page_number;

  for (const chunkId of field.references ?? []) {
    const page = chunkPageByChunkId.get(chunkId);
    if (page != null) return page;
  }

  // The model reports a page for every field, boxed or not. Weakest of the
  // three — nothing corroborates it, and it buys no highlight — but a field
  // that knows its page can be reviewed on that page instead of sitting in a
  // bucket whose only exit was accepting the value unseen.
  if (field.source_page != null) return field.source_page;

  return null;
}
